import type { ReportAggregateBucket } from '@/api/models';

/**
 * The platform's contractual ceiling of card charges per calendar year.
 * The annual counter exists to watch this number — it is a business limit,
 * not something the API reports.
 */
export const ANNUAL_CARD_OPERATIONS_LIMIT = 300000;

/** Fraction of the annual limit at which the counter shifts to its warning presentation. */
export const ANNUAL_WARNING_RATIO = 0.8;

export interface AnnualUsage {
  /** Effective charges counted so far this year — only successful intents count against the limit. */
  count: number;
  limit: number;
  /** `count / limit` — may exceed 1 once the limit is passed. */
  ratio: number;
  /** Charges left under the limit, floored at zero. */
  remaining: number;
  /** `true` from {@link ANNUAL_WARNING_RATIO} upwards. */
  warning: boolean;
}

/**
 * Reduces an aggregates response to the annual usage figures. The limit
 * counts EFFECTIVE charges — `successCount`, the intents actually charged —
 * never every started intent: an abandoned or declined attempt consumes
 * nothing. Every charge lands in exactly one bucket of a single-dimension
 * grouping (the per-currency split included), so summing `successCount`
 * across buckets is the total.
 */
export function summarizeAnnualUsage(
  buckets: ReportAggregateBucket[],
  limit: number = ANNUAL_CARD_OPERATIONS_LIMIT,
): AnnualUsage {
  const count = buckets.reduce((sum, bucket) => sum + bucket.successCount, 0);
  const ratio = limit > 0 ? count / limit : 0;
  return {
    count,
    limit,
    ratio,
    remaining: Math.max(0, limit - count),
    warning: ratio >= ANNUAL_WARNING_RATIO,
  };
}

/** RFC 3339 instant of 1 January of `now`'s local year — the annual counter's fixed window start. */
export function currentYearStartIso(now: Date = new Date()): string {
  return new Date(now.getFullYear(), 0, 1).toISOString();
}

/** The statistics page's breakdown dimensions — the PillTabs values. */
export type StatsGrouping = 'tenant' | 'tag' | 'all';

/**
 * The breakdown table's column sets. The simple view answers "who charged
 * how much": bucket, currency, charges and amount. The extended view adds
 * the started-operations count and the success rate for funnel reading.
 */
export type BreakdownViewMode = 'simple' | 'extended';

/**
 * One row of the breakdown table and of the CSV export: a bucket label plus
 * ONE currency's figures. A bucket that saw several currencies contributes
 * one row per currency — amounts are never summed across currencies.
 */
export interface BreakdownRow {
  /** Already-resolved display label (tenant name+code, tag, "Sin etiqueta", "Totales"). */
  label: string;
  currency: string;
  totalCount: number;
  successCount: number;
  /** `successCount / totalCount`, 0 when the row has no operations. */
  successRatio: number;
  totalAmount: number;
}

/**
 * Reduces aggregate buckets to breakdown rows keyed by `(label, currency)`,
 * sorted by charges descending (label, then currency, break ties).
 * `labelOf` resolves each bucket's display label — a constant label (the
 * "Todos" view) folds every bucket into per-currency total rows, which is
 * exactly the never-sum-across-currencies rule expressed structurally.
 */
export function buildBreakdownRows(
  buckets: ReportAggregateBucket[],
  labelOf: (bucket: ReportAggregateBucket) => string,
): BreakdownRow[] {
  const rows = new Map<string, BreakdownRow>();
  for (const bucket of buckets) {
    const label = labelOf(bucket);
    const key = `${label}\u0000${bucket.currency}`;
    const row = rows.get(key);
    if (row) {
      row.totalCount += bucket.totalCount;
      row.successCount += bucket.successCount;
      row.totalAmount += bucket.totalAmount;
    } else {
      rows.set(key, {
        label,
        currency: bucket.currency,
        totalCount: bucket.totalCount,
        successCount: bucket.successCount,
        successRatio: 0,
        totalAmount: bucket.totalAmount,
      });
    }
  }
  const list = [...rows.values()];
  for (const row of list) {
    row.successRatio = row.totalCount > 0 ? row.successCount / row.totalCount : 0;
  }
  list.sort(
    (a, b) =>
      b.successCount - a.successCount || a.label.localeCompare(b.label) || a.currency.localeCompare(b.currency),
  );
  return list;
}

/** The breakdown table's sortable dimensions — one per column. */
export type BreakdownSortKey =
  | 'label'
  | 'currency'
  | 'totalCount'
  | 'successCount'
  | 'successRatio'
  | 'totalAmount';

export type SortDirection = 'asc' | 'desc';

/**
 * Returns the rows ordered by one column, without mutating the input. Text
 * columns compare alphabetically, numeric columns numerically; the amount
 * column compares the raw minor-unit figures — an ordering aid, never a
 * cross-currency sum. The incoming order breaks ties (`Array.prototype.sort`
 * is stable).
 */
export function sortBreakdownRows(
  rows: BreakdownRow[],
  key: BreakdownSortKey,
  direction: SortDirection,
): BreakdownRow[] {
  const factor = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const compared =
      key === 'label' || key === 'currency' ? a[key].localeCompare(b[key]) : a[key] - b[key];
    return compared * factor;
  });
}

/**
 * One line of the totals footer (and of the CSV's closing lines). A line
 * with a `currency` totals that currency's rows; the `currency: null` line —
 * present only when several currencies are displayed — carries the overall
 * operation figures and no amounts, because amounts are never summed across
 * currencies.
 */
export interface BreakdownTotalsLine {
  currency: string | null;
  totalCount: number;
  successCount: number;
  /** `successCount / totalCount` of the line — a ratio of sums, never an average of the rows' percentages. */
  successRatio: number;
  totalAmount: number | null;
}

/**
 * Totals over the CURRENTLY displayed rows (grouping and date filters have
 * already shaped them): one line per currency, alphabetically, plus — only
 * when more than one currency is present — a closing overall line summing
 * the operation counts across every currency, its amounts left null.
 */
export function buildBreakdownTotals(rows: BreakdownRow[]): BreakdownTotalsLine[] {
  const byCurrency = new Map<string, BreakdownTotalsLine>();
  let totalCount = 0;
  let successCount = 0;

  for (const row of rows) {
    totalCount += row.totalCount;
    successCount += row.successCount;
    const line = byCurrency.get(row.currency);
    if (line) {
      line.totalCount += row.totalCount;
      line.successCount += row.successCount;
      line.totalAmount = (line.totalAmount ?? 0) + row.totalAmount;
    } else {
      byCurrency.set(row.currency, {
        currency: row.currency,
        totalCount: row.totalCount,
        successCount: row.successCount,
        successRatio: 0,
        totalAmount: row.totalAmount,
      });
    }
  }

  const lines = [...byCurrency.values()].sort((a, b) => a.currency!.localeCompare(b.currency!));
  for (const line of lines) {
    line.successRatio = line.totalCount > 0 ? line.successCount / line.totalCount : 0;
  }
  if (lines.length > 1) {
    lines.push({
      currency: null,
      totalCount,
      successCount,
      successRatio: totalCount > 0 ? successCount / totalCount : 0,
      totalAmount: null,
    });
  }
  return lines;
}

/** Already-translated CSV column headers; the view decides which are written. */
export interface BreakdownCsvHeaders {
  bucket: string;
  currency: string;
  totalCount: string;
  successCount: string;
  successRate: string;
  totalAmount: string;
}

function csvField(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Fixed-decimal number with a comma decimal separator and no grouping — what es-ES Excel parses as numeric. */
function csvNumber(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',');
}

/** Already-translated labels for the CSV's closing totals lines. */
export interface BreakdownCsvTotalsLabels {
  /** Label of a per-currency totals line. */
  totals: string;
  /** Label of the overall line closing a multi-currency export. */
  overall: string;
}

/**
 * Renders breakdown rows as a CSV for es-ES Excel: UTF-8 BOM prefix,
 * semicolon separator, CRLF line endings, comma decimals, amounts in major
 * units. The `view` decides the columns — the export always mirrors the
 * table on screen: simple writes bucket, currency, charges and amount;
 * extended adds the started-operations count and the success rate (so the
 * overall line's ratio-of-sums percentage appears only there). One line per
 * `(bucket, currency)` row, closed by the same totals lines the table pins
 * at the bottom (the overall line's amount cell stays empty).
 */
export function buildBreakdownCsv(
  rows: BreakdownRow[],
  headers: BreakdownCsvHeaders,
  totals: BreakdownTotalsLine[],
  totalsLabels: BreakdownCsvTotalsLabels,
  view: BreakdownViewMode,
): string {
  const extended = view === 'extended';
  const pick = (simple: string[], extra: Record<number, string>): string[] => {
    if (!extended) return simple;
    const out = [...simple];
    // Insert extended cells at their column positions: started after currency, rate before amount.
    out.splice(2, 0, extra[0]!);
    out.splice(4, 0, extra[1]!);
    return out;
  };
  const lines = [
    pick(
      [headers.bucket, headers.currency, headers.successCount, headers.totalAmount],
      { 0: headers.totalCount, 1: headers.successRate },
    ),
    ...rows.map((row) =>
      pick(
        [row.label, row.currency, String(row.successCount), csvNumber(row.totalAmount / 100, 2)],
        { 0: String(row.totalCount), 1: csvNumber(row.successRatio * 100, 1) },
      ),
    ),
    ...totals.map((line) =>
      pick(
        [
          line.currency === null ? totalsLabels.overall : totalsLabels.totals,
          line.currency ?? '',
          String(line.successCount),
          line.totalAmount === null ? '' : csvNumber(line.totalAmount / 100, 2),
        ],
        { 0: String(line.totalCount), 1: csvNumber(line.successRatio * 100, 1) },
      ),
    ),
  ];
  return `\ufeff${lines.map((line) => line.map(csvField).join(';')).join('\r\n')}\r\n`;
}

/** `YYYY-MM-DD` of an RFC 3339 instant in LOCAL time — filenames must name the day the user picked, not its UTC shift. */
function localDateStamp(iso: string): string {
  const parsed = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

const GROUPING_FILENAME_PART: Record<StatsGrouping, string> = {
  tenant: 'establecimiento',
  tag: 'etiqueta',
  all: 'todos',
};

/** `estadisticas-<grouping>-<from>-<to>.csv`; an open end falls back to `inicio` / today. */
export function statisticsCsvFilename(
  grouping: StatsGrouping,
  dateFrom?: string,
  dateTo?: string,
  now: Date = new Date(),
): string {
  const from = dateFrom ? localDateStamp(dateFrom) : 'inicio';
  const to = dateTo ? localDateStamp(dateTo) : localDateStamp(now.toISOString());
  return `estadisticas-${GROUPING_FILENAME_PART[grouping]}-${from}-${to}.csv`;
}
